import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  AfterViewInit,
  Component,
  ContentChild,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AssetUIModel,
  CanPipe,
  DefaultAllStatusList,
  DefaultAssetType,
  OptionsLabelPipe,
  StorageUtilityLib,
  TableActionObject,
  TableColumn,
  TableColumnType,
  USER_SESSION_TOKEN,
  UserSession,
  accessProject,
  accessTask,
  isAssetPlannedOrUnpublished,
} from '@thehubz/client/core';
import { AnyType } from '@thehubz/shared/model';
import { Subscription, interval, of, timer } from 'rxjs';
import { filter, map, pairwise, throttleTime } from 'rxjs/operators';
import { ListStateService } from './list-state.service';

@Component({
  selector: 'sp-custom-generic-table',
  templateUrl: './custom-generic-table.component.html',
  styleUrls: ['./custom-generic-table.component.scss'],
})
export class CustomGenericTableComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @ContentChild('rowActionMenu', { read: TemplateRef }) rowActionMenu!: TemplateRef<AnyType>;
  @ContentChild('headerActionMenu', { read: TemplateRef }) headerActionMenu!: TemplateRef<Element>;
  // table columns defined as per TableColumn model
  @Input() tableCols: TableColumn[] = [];

  @Input() tableType!: string;
  @Input() rowHeightAuto?: boolean;
  // columns to display in table
  displayedColumns: string[] = [];

  loggedInUserId = '';
  // table datasource
  @Input() tableData!: AnyType;

  @Input() offset!: number;

  // if action column required
  @Input() actionColumn!: boolean;

  @Input() actionType: 'menu' | 'action-button' = 'menu';

  @Input() deleteFlag?: boolean;
  @Input() duplicateFlag?: boolean;
  @Input() editFlag?: boolean;
  @Input() itemSize? = 48;
  // Table name for data test-ids
  @Input() tableName?: string = '';
  // if action column required
  @Input() expandActionColumn!: boolean;
  @Input() configObject!: AnyType;

  // send event from table component to its parent component
  // it emit the data rendered in the column
  @Output() clickEdit = new EventEmitter<AnyType>();
  @Output() clickExpand = new EventEmitter<number>();
  @Output() clickCollapse = new EventEmitter<number>();
  @Output() clickDuplicate = new EventEmitter<AnyType>();
  @Output() clickDelete = new EventEmitter<AnyType>();
  @Output() clickLoadMore = new EventEmitter<{ offset: number; anchorIndex?: number }>();
  @Output() clickSortFilter = new EventEmitter<AnyType>();
  @Output() clickDraftStatus = new EventEmitter<AnyType>();
  @Output() clickEditEvent = new EventEmitter<AnyType>();
  @Output() clickAssetDetails = new EventEmitter<AnyType>();
  @Output() checkboxChanged = new EventEmitter<AnyType>();
  // for sort and pagination
  @ViewChild(MatSort, { static: true }) sort!: MatSort;
  @ViewChildren(MatCheckbox) checkboxes!: MatCheckbox[];
  // @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;

  AccessProject = accessProject;
  AccessTask = accessTask;

  @ViewChild('scroller') scroller!: CdkVirtualScrollViewport;
  limit = 48;
  flag = false;
  avatarSize = 30;
  avatarFontSize = 12;
  showNameToolTip = true;
  tableColumnType = TableColumnType;

  defaultAllStatusList: AnyType = DefaultAllStatusList;
  tablePositions: AnyType = [];
  currentUrl!: string;
  param!: string;
  selectedItems: AnyType = [];
  isScrolledToBottom = false;

  private scrollerSubscription: Subscription | undefined;

  constructor(
    @Inject(USER_SESSION_TOKEN) private userSessionToken: string,
    private ngZone: NgZone,
    private router: Router,
    private storageUtility: StorageUtilityLib,
    private route: ActivatedRoute,
    private canPipe: CanPipe,
    private optionsLabelPipe: OptionsLabelPipe,
    private listState: ListStateService,
  ) {
    this.currentUrl = this.router.url;
  }

  ngOnInit() {
    const userSession = this.storageUtility.getLocalStorage<UserSession>(this.userSessionToken);
    this.loggedInUserId = userSession?.userData._id;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // adding action column if actionColumn is true
    if (this.actionColumn && changes['tableCols'] && changes['tableCols'].currentValue) {
      const idx = this.tableCols.findIndex((col) => {
        return col.type === TableColumnType.action;
      });
      if (idx === -1) {
        if (this.tableType && this.tableType === 'agency') {
          this.tableCols.push({
            columnDef: 'actions',
            type: TableColumnType.action,
            header: '',
            class: 'w-[15%]',
            visible: true,
          });
        } else {
          this.tableCols.push(TableActionObject);
        }
      }
      const statusIndex = this.tableCols.findIndex((col) => {
        return col.columnDef === 'status';
      });
      const assetTypeIndex = this.tableCols.findIndex((col) => {
        return col.columnDef === 'assetType';
      });
      if (statusIndex != -1) {
        this.tableCols[statusIndex].options = this.defaultAllStatusList;
      }
      if (assetTypeIndex != -1) {
        this.tableCols[assetTypeIndex].options = DefaultAssetType;
      }
      this.displayedColumns = this.tableCols.map((c) => c.columnDef);
    }
  }

  scrollToTop() {
    this.offset = 0;
    this.scroller.scrollToIndex(0);
  }

  setViewportData(event: AnyType) {
    if (sessionStorage['tablePositions']) {
      const data = JSON.parse(sessionStorage['tablePositions']);
      const tableIndex = data.findIndex((obj: { url: string }) => obj.url === this.currentUrl);
      if (tableIndex !== -1) {
        if (data[tableIndex].item !== event) {
          data[tableIndex].item = event;
          data[tableIndex].items = this.tableData;
        }
      } else {
        data.push({ url: this.currentUrl, item: event, items: this.tableData });
      }
      this.tablePositions = data;
      sessionStorage.setItem('tablePositions', JSON.stringify(this.tablePositions));
    }
  }

  isObject(val: AnyType): boolean {
    return typeof val === 'object';
  }

  isArray(data: AnyType): boolean {
    return Array.isArray(data);
  }

  fetchData(data: AnyType, options: AnyType) {
    let returnString = '';
    data.forEach((value: AnyType) => {
      if (returnString != '') {
        returnString += ', ';
      }
      const transformedValue = value ? (options ? this.optionsLabelPipe.transform(value, options) : value.name ? value.name : '') : '';
      returnString += transformedValue;
    });
    return returnString;
  }

  setTablePosition() {
    const data = JSON.parse(sessionStorage['tablePositions']);
    const tableIndex = data.findIndex((obj: { url: string }) => obj.url === this.currentUrl);
    const index = data[tableIndex].item;
    if (index) {
      timer(0).subscribe(() => {
        const newInterval = interval(10).subscribe(() => {
          const lastItem = this.scroller.getRenderedRange().end;
          if (lastItem < index) {
            this.scrollToIndex(lastItem);
            return;
          } else {
            this.scrollToIndex(index);
            timer(100).subscribe(() => {
              this.setViewportData(index);
            });
            newInterval.unsubscribe();
          }
        });
      });
    }
  }

  ngAfterViewInit(): void {
    // Restore scroll position if exists
    if (this.listState.scrollOffset) {
      setTimeout(() => {
        this.scroller.scrollToOffset(this.listState.scrollOffset);
        this.listState.saveOffsetState(0);
      }, 0);
    }
    if (this.listState.selectedIndex) {
      setTimeout(() => {
        const targetIndex = this.listState.selectedIndex;
        this.scroller.scrollToIndex(targetIndex, 'smooth');
        this.listState.selectedIndex = 0;
      }, 0);
    }

    if (this.scroller) {
      this.scrollerSubscription = this.scroller
        .elementScrolled()
        .pipe(
          map(() => this.scroller.measureScrollOffset('bottom')),
          pairwise(),
          filter(([y1, y2]) => y2 < y1 && y2 < 100),
          throttleTime(500),
        )
        .subscribe(() => {
          this.isScrolledToBottom = true;
          this.ngZone.run(() => {
            this.offset += 1;
            this.fetchMore();
          });
        });

      this.scrollerSubscription = this.scroller
        .elementScrolled()
        .pipe(
          map(() => this.scroller.measureScrollOffset('top')),
          pairwise(),
          filter(([y1, y2]) => y1 > y2 && y2 < 2),
          throttleTime(500),
        )
        .subscribe(() => {
          this.isScrolledToBottom = true;
          this.ngZone.run(() => {
            const anchorIndex = this.scroller.getRenderedRange().start;
            this.offset -= 1;
            this.fetchMore(anchorIndex);
          });
        });
    }
  }

  scrollToIndex(index: number, behavior: ScrollBehavior = 'instant') {
    this.scroller.scrollToIndex(index, behavior);
  }

  // Details view
  detailsView(tableData: AnyType, i: number, disableClick = false) {
    if (disableClick) return;
    if (tableData.status === 'Draft') {
      if (this.canPipe.transform(this.AccessTask(tableData), 'create')) {
        this.clickDraftStatus.emit(tableData);
      }
      return;
    }
    if (tableData.objectType === 'agencies') {
      return;
    }
    if (tableData.type === 'Project' || tableData.objectType === 'subProjects') {
      const checkIntex = this.router.url.indexOf('projects/detail');
      if (checkIntex > -1) {
        this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
          this.router.navigate(['/projects/detail', tableData._id]);
        });
      } else {
        this.router.navigate(['/projects/detail', tableData._id]);
      }
    } else if (
      (this.configObject && this.configObject.callFrom === 'project') ||
      tableData.objectType === 'subTasks' ||
      tableData.objectType === 'tasks' ||
      this.tableType === 'tasks'
    ) {
      const checkIntex = this.router.url.indexOf('tasks/detail');
      if (checkIntex > -1) {
        this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
          this.router.navigate(['/tasks/', 'detail', tableData._id]);
        });
      } else {
        this.router.navigate(['/tasks/', 'detail', tableData._id]);
      }
    } else if (tableData.objectType === 'workflow') {
      this.router.navigate(['/admin/workflows/details', tableData._id]);
    } else if (tableData.assets) {
      this.router.navigate(['/collections/', 'collection-asset-list', tableData._id]);
    } else if (tableData.assetType) {
      if (this.isScrolledToBottom) {
        this.listState.saveScrolledState({ selectedId: tableData._id, selectedIndex: i - this.offset * this.limit });
      } else {
        const offset = this.scroller.measureScrollOffset();
        this.listState.saveOffsetState(offset);
      }
      this.clickAssetDetails.emit({ tableData, i });
    } else {
      if (!this.configObject || (this.configObject && this.configObject.callFrom !== 'unapproved-userList')) {
        this.router.navigate(['detail', tableData._id], {
          relativeTo: this.route,
        });
      }
    }
  }

  navigateLink(evt: Event, link: string, obj?: AnyType) {
    let route = link;
    evt.stopPropagation();
    if (obj) {
      route = link.replace('{id}', obj._id || obj.id);
    }
    this.router.navigate([route]);
  }

  duplicateRecord(evt: Event, tableData: AnyType) {
    evt.stopPropagation();
    this.clickDuplicate.emit(tableData);
  }

  editView(evt: Event, tableData: AnyType) {
    evt.stopPropagation();
    if (tableData.objectType === 'workflow') {
      this.router.navigate(['/admin/workflows/edit', tableData._id]);
    } else if (tableData.objectType === 'agencies') {
      this.clickEditEvent.emit(tableData);
    } else if (tableData.categoryName === 'brandModell') {
      this.clickEditEvent.emit(tableData);
    }
  }

  // roles access
  deleteAccess(data: AnyType) {
    if (data.type === 'Project' || data.type === 'Sub-Project') {
      return this.canPipe.transform(this.AccessProject(data), 'delete');
    } else if (data.taskType === 'Task' || data.taskType === 'Sub-Task') {
      return this.canPipe.transform(this.AccessTask(data), 'delete');
    }
    return true;
  }

  // delete action
  deleteDialog(data: AnyType) {
    this.clickDelete.emit(data);
  }

  expandRow(evt: Event, index: number) {
    evt.stopPropagation();
    this.clickExpand.emit(index);
  }

  collapseRow(evt: Event, index: number) {
    evt.stopPropagation();
    this.clickCollapse.emit(index);
  }

  fetchMore(anchorIndex?: number) {
    this.clickLoadMore.emit({ offset: this.offset, anchorIndex });
  }

  setFilterSelected(index: number, sortValue: number) {
    this.tableCols[index]['filterSelected'] = sortValue;
    let key = this.tableCols[index]['columnDef'];
    if (this.tableCols[index]['showValue']) {
      key = key + '.' + this.tableCols[index]['showValue'];
    }
    return { [key]: sortValue };
  }

  selectedFilter(index: number) {
    this.resetAllFilters(index);
    if (this.tableCols.length > 0) {
      this.offset = 0;
      switch (this.tableCols[index]['filterSelected']) {
        case 1: {
          const sortObject = this.setFilterSelected(index, -1);
          this.clickSortFilter.emit(sortObject);
          break;
        }
        case -1: {
          this.tableCols[index]['filterSelected'] = 0;
          const sortObject = { updatedAt: -1 };
          this.clickSortFilter.emit(sortObject);
          break;
        }
        default: {
          const sortObject = this.setFilterSelected(index, 1);
          this.clickSortFilter.emit(sortObject);
          break;
        }
      }
    }
  }

  resetAllFilters(index: number) {
    if (this.tableCols.length > 0) {
      for (let i = 0; i < this.tableCols.length; i++) {
        if (i !== index && this.tableCols[i]['filterSelected']) {
          this.tableCols[i]['filterSelected'] = 0;
        }
      }
    }
  }

  showUsername$(data: string[]) {
    if (data.length) {
      let username = '';
      data.forEach((user: AnyType) => {
        if (user.email) {
          username += user.email + ' \n';
        }
      });
      return of(username);
    }
    return of('');
  }

  checkboxOnChange(data: any) {
    this.checkboxChanged.emit(data);
  }

  isAssetPlannedOrUnpublished(asset: AssetUIModel): boolean {
    return isAssetPlannedOrUnpublished(asset);
  }

  ngOnDestroy(): void {
    if (this.scrollerSubscription) {
      this.scrollerSubscription.unsubscribe();
    }
  }
}
